import { inject, Injectable } from '@angular/core';
import { E_MasterListName } from '../../../enums/master-list-name.enum';
import { I_ConfigObservationResponse } from '../../../models/display/items/observation/api/config-observation.response';
import { C_ObservationAlarmData } from '../../../models/display/items/observation/domain/observation-alarm-data.model';
import { E_SocketMessageType } from '../../../enums/socket/socket-message-type.enum';
import { C_DisplayData } from '../../../models/display/domain/display.model';
import { bufferTime, filter, Subject } from 'rxjs';
import { I_ObservationResponse } from 'src/app/core/models/display/items/observation/api/observation.response';
import { I_DisplayResponse } from 'src/app/core/models/display/api/display.response';
import { AuthService } from '../../auth.service';
import { DisplaysService } from '../../displays.service';
import { E_HomeBanner } from 'src/app/core/enums/home-banner-type.enum';
import { I_AdmissionResponse } from 'src/app/core/models/display/items/admission/api/admission.response';
import { I_DischargeResponse } from 'src/app/core/models/display/items/discharge/api/discharge.response';
import { C_DischargeItem } from 'src/app/core/models/display/items/discharge/domain/discharge.model';
import { C_BannerItem } from 'src/app/core/models/display/items/home/domain/home-config.model';
import { I_NoticeResponse } from 'src/app/core/models/display/items/notices/api/notices.response';
import { C_Person } from 'src/app/core/models/display/items/patient/domain/person.model';
import { I_PointOfCareResponse } from 'src/app/core/models/display/items/point-of-care/api/point-of-care-config.response';
import { I_UnitResponse } from 'src/app/core/models/unit/api/unit.response';
import { C_Unit } from 'src/app/core/models/unit/domain/unit.model';
import { I_MasterListItemResponse, I_MasterListResponse } from 'src/app/core/models/unit/items/master-lists/api/master-list.response';

@Injectable({ providedIn: 'root' })
export class DisplaySocketProcessorService {

    private auth = inject(AuthService);
    private displayService = inject(DisplaysService);

    private observationBuffer$ = new Subject<{ message: any, display: C_DisplayData }>();
    private groupedOobservationBuffer$ = new Subject<{ message: any, display: C_DisplayData }>();

    private treatmentBuffer$ = new Subject<{ message: any, display: C_DisplayData }>();

    constructor() {
        this.observationBuffer$.pipe(
            bufferTime(250, null, 50),
            filter(batch => batch.length > 0)
        ).subscribe(batch => {
            this.processObservationBatch(batch);
        });
        this.groupedOobservationBuffer$.pipe(
            bufferTime(250, null, 20),
            filter(batch => batch.length > 0)
        ).subscribe(batch => {
            this.processGroupedObservationBatch(batch);
        });
        this.treatmentBuffer$.pipe(
            bufferTime(250, null, 20),
            filter(batch => batch.length > 0)
        ).subscribe(batch => {
            this.processTreatmentBatch(batch);
        });
    }

    public process(type: E_SocketMessageType, message: any, display: C_DisplayData): void {
        switch (type) {
            case E_SocketMessageType.CONFIG_OBSERVATION:
                display.configObservationList.setBulkConfigObs(message as I_ConfigObservationResponse[]);
                break;
            case E_SocketMessageType.OBSERVATION:
                this.observationBuffer$.next({ message, display });
                break;
            case E_SocketMessageType.GROUPED_OBSERVATION:
                this.groupedOobservationBuffer$.next({ message, display });
                break;
            case E_SocketMessageType.ALARM:
                this.handleAlarm(message, display);
                break;
            case E_SocketMessageType.UPDATE_POINTOFCARE:
                message = message as I_PointOfCareResponse;
                display.displayInfo.pointOfCares.setPocFromResponse(message);
                display.patientList.setPatient(message.patient);
                break;
            // case E_SocketMessageType.DELETE_POINTOFCARE: //TODO
            //     message = 
            //     display.displayInfo.pointOfCares.deletePointOfCare(message.id);
            case E_SocketMessageType.NEW_POINTOFCARE:
                message = message as I_PointOfCareResponse;
                this.handleNewPoc(message, display);
                break;
            case E_SocketMessageType.UPDATE_PATIENT:
            case E_SocketMessageType.NEW_PATIENT:
                display.patientList.setPatient(message);
                display.dischargeList.updatePatient(message);
                // display.displayInfo.pointOfCares.forceUpdateDetection();
                break;

            case E_SocketMessageType.DELETE_PATIENT:
                display.patientList.deletePatient(message.id);
                display.dischargeList.deletePatient(message.id);
                break;
            case E_SocketMessageType.PERSON_DATA:
                message = message as {
                    id: string,
                    data: C_Person,
                };
                display.patientList.setPerson(message);
                Object.entries(display.displayInfo.homeBanner.list).forEach(([k, v]: [string, C_BannerItem]) => {
                    if (v.type != E_HomeBanner.Outputs) {
                        Object.entries(v.discharges).forEach(([k, v]: [string, C_DischargeItem]) => {
                            if (v.patient?.id == message.id) {
                                v.patient!.person = new C_Person(message.data)
                                v.forceUpdateDetection();
                            }
                        });
                        return;
                    }
                });
                display.displayInfo.pointOfCares.forceUpdateDetection()
                break;
            case E_SocketMessageType.DISPLAY_LIST:
                display.displayInfo.displaySectionList = message;
                display.displayInfo.forceUpdateDetection();
                break;
            case E_SocketMessageType.UPDATE_UNIT:
                message = message as I_UnitResponse;
                display.unit.updateFromUnit(new C_Unit(message));
                break;
            case E_SocketMessageType.TREATMENT:
                this.treatmentBuffer$.next({ message, display });
                break;
            case E_SocketMessageType.UPDATE_TREATMENT:
                // display.displayInfo.patients.updateTreatment(message);
                break;
            case E_SocketMessageType.BEACON:
                break;
            // CONFIG CHANGES: PENDING REFACTOR IN V3
            case E_SocketMessageType.UPDATE_DETAIL_DISPLAY_CONFIG:
            case E_SocketMessageType.UDATE_CHART_CONFIG:
            case E_SocketMessageType.UPDATE_DISPLAY_CONFIG:
            case E_SocketMessageType.UPDATE_CARD_DISPLAY_CONFIG:
            case E_SocketMessageType.UPDATE_SMART_DISPLAY_CONFIG:
            case E_SocketMessageType.UPDATE_NURSE_DISPLAY_CONFIG:
                this.forceRefresh();
                break;
            case E_SocketMessageType.UPDATE_PASSWORD:
                this.auth.passwordUpdated();
                break;
            case E_SocketMessageType.LOCK_USER:
                this.auth.lockUser(message);
                break;
            case E_SocketMessageType.DISABLE_USER:
                this.auth.disableUser();
                break;
            case E_SocketMessageType.UPDATE_AUTHORITIES:
                const updtAuth = message as I_DisplayResponse[];
                this.auth.updateAuthorities(updtAuth);
                this.displayService.updateDisplaysAuthoritiesSync(updtAuth);
                this.auth.refreshToken()
                break;
            case E_SocketMessageType.NEW_ADMISSION:
                message = message as I_AdmissionResponse;
                // Object.entries(display.displayInfo.homeBanner.list).forEach(([k, v]: [string, C_BannerItem]) => {
                //     if (v.type == E_HomeBanner.Inputs) {
                //         v.addAdmission(message);
                //         return;
                //     }
                // });
                display.admissionList.addNewAdmission(message);
                // display.displayInfo.pointOfCares.addAdmission(message);
                break;
            case E_SocketMessageType.UPDATE_ADMISSION:
                // message = message as I_AdmissionResponse;
                // Object.entries(display.displayInfo.homeBanner.list).forEach(([k, v]: [string, C_BannerItem]) => {
                //     if (v.type == E_HomeBanner.Inputs) {
                //         v.updateAdmission(message);
                //         return;
                //     }
                // });
                display.admissionList.updateAdmission(message);
                display.displayInfo.pointOfCares.updateAdmission(message);
                break;
            case E_SocketMessageType.DELETE_ADMISSION:
                message = message as I_AdmissionResponse;
                // display.displayInfo.pointOfCares.deleteAdmission(message);
                // Object.entries(display.displayInfo.homeBanner.list).forEach(([k, v]: [string, C_BannerItem]) => {
                //     if (v.type == E_HomeBanner.Inputs) {
                //         v.deleteAdmission(message);
                //         return;
                //     }
                // });
                display.admissionList.deleteAdmission(message);
                break;
            case E_SocketMessageType.NEW_DISCHARGE:
            case E_SocketMessageType.UPDATE_DISCHARGE:
                message = message as I_DischargeResponse;
                display.displayInfo.pointOfCares.addDischarge(message);
                Object.entries(display.displayInfo.homeBanner.list).forEach(([k, v]: [string, C_BannerItem]) => {
                    if (v.type == E_HomeBanner.Outputs) {
                        v.addDischarge(message);
                        return;
                    }
                });
                display.dischargeList.addNewDischarge(message);
                break;
            case E_SocketMessageType.DELETE_DISCHARGE:
                message = message as I_DischargeResponse;
                display.displayInfo.pointOfCares.deleteDischarge(message);
                Object.entries(display.displayInfo.homeBanner.list).forEach(([k, v]: [string, C_BannerItem]) => {
                    if (v.type == E_HomeBanner.Outputs) {
                        v.deleteDischarge(message);
                        return;
                    }
                });
                display.dischargeList.deleteDischarge(message);
                break;
            case E_SocketMessageType.NEW_NOTICE:
            case E_SocketMessageType.UPDATE_NOTICE:
                message = message as I_NoticeResponse;
                Object.entries(display.displayInfo.homeBanner.list).forEach(([k, v]: [string, C_BannerItem]) => {
                    if (v.type == E_HomeBanner.Notices) {
                        v.addNotice(message);
                        return;
                    }
                });
                break;
            case E_SocketMessageType.DELETE_NOTICE:
                message = message as I_NoticeResponse;
                Object.entries(display.displayInfo.homeBanner.list).forEach(([k, v]: [string, C_BannerItem]) => {
                    if (v.type == E_HomeBanner.Notices) {
                        v.deleteNotice(message);
                        return;
                    }
                });
                break;
            case E_SocketMessageType.DELETE_MASTER_LIST_ITEM:
            case E_SocketMessageType.ADD_MASTER_LIST_ITEM:
            case E_SocketMessageType.UPDATE_MASTER_LIST_ITEM:
                message = message as I_MasterListItemResponse;
                if (message == null) return;
                switch (message.listType) {
                    case E_MasterListName.ACCESS_CONTROL:
                        display.unit.accessControlList.manageItem(message, type);
                        break;
                    case E_MasterListName.ALLERGY:
                        display.unit.allergyList.manageItem(message, type);
                        break;
                    case E_MasterListName.DOCTOR_LIST:
                        display.unit.doctorList.manageItem(message, type);
                        break;
                    case E_MasterListName.DOCTOR_TYPE:
                        display.unit.doctorTypeList.manageItem(message, type);
                        break;
                    case E_MasterListName.ALTABLE:
                        display.unit.altableList.manageItem(message, type);
                        break;
                    case E_MasterListName.DIAGNOSIS:
                        display.unit.diagnosisList.manageItem(message, type);
                        break;
                    case E_MasterListName.INSULATION:
                        display.unit.insulationList.manageItem(message, type);
                        break;
                    case E_MasterListName.ORIGIN:
                        display.unit.originList.manageItem(message, type);
                        break;
                    case E_MasterListName.MOBILITY:
                        display.unit.mobilityList.manageItem(message, type);
                        break;
                    case E_MasterListName.DESTINATION:
                        display.unit.destinationList.manageItem(message, type);
                        break;
                    case E_MasterListName.PROCEDURE:
                        display.unit.procedureList.manageItem(message, type);
                        break;
                    case E_MasterListName.TEST:
                        display.unit.testList.manageItem(message, type);
                        break;
                    case E_MasterListName.THERAPEUTIC_CELLING:
                        display.unit.therapeuticCeilingList.manageItem(message, type);
                        break;
                    case E_MasterListName.VISIT:
                        display.unit.visitList.manageItem(message, type);
                        break;
                    case E_MasterListName.SERVICE:
                        display.unit.serviceList.manageItem(message, type);
                        break;
                    case E_MasterListName.TREATMENT:
                        display.unit.treatmentList.manageItem(message, type);
                        break;
                    case E_MasterListName.PATIENT_STATUS:
                        display.unit.patientStatusList.manageItem(message, type);
                        break;
                    case E_MasterListName.LANGUAGE_BARRIER_LIST:
                        display.unit.languageBarrierList.manageItem(message, type);
                        break;
                    case E_MasterListName.PASSIVE_SITTING:
                        display.unit.passiveSittingList.manageItem(message, type);
                        break;
                }
                display.unit?.forceUpdateDetection();
                break;
            case E_SocketMessageType.NEW_MASTER_LIST:
            case E_SocketMessageType.UPDATE_MASTER_LIST:
                message = message as I_MasterListResponse;
                if (message == null) return;
                switch (message.listType) {
                    case E_MasterListName.ACCESS_CONTROL:
                        display.unit.accessControlList.initializeFromJson(message);
                        break;
                    case E_MasterListName.ALLERGY:
                        display.unit.allergyList.initializeFromJson(message);
                        break;
                    case E_MasterListName.DOCTOR_LIST:
                        display.unit.doctorList.initializeFromJson(message);
                        break;
                    case E_MasterListName.DOCTOR_TYPE:
                        display.unit.doctorTypeList.initializeFromJson(message);
                        break;
                    case E_MasterListName.ALTABLE:
                        display.unit.altableList.initializeFromJson(message);
                        break;
                    case E_MasterListName.DIAGNOSIS:
                        display.unit.diagnosisList.initializeFromJson(message);
                        break;
                    case E_MasterListName.INSULATION:
                        display.unit.insulationList.initializeFromJson(message);
                        break;
                    case E_MasterListName.ORIGIN:
                        display.unit.originList.initializeFromJson(message);
                        break;
                    case E_MasterListName.MOBILITY:
                        display.unit.mobilityList.initializeFromJson(message);
                        break;
                    case E_MasterListName.DESTINATION:
                        display.unit.destinationList.initializeFromJson(message);
                        break;
                    case E_MasterListName.PROCEDURE:
                        display.unit.procedureList.initializeFromJson(message);
                        break;
                    case E_MasterListName.TEST:
                        display.unit.testList.initializeFromJson(message);
                        break;
                    case E_MasterListName.THERAPEUTIC_CELLING:
                        display.unit.therapeuticCeilingList.initializeFromJson(message);
                        break;
                    case E_MasterListName.VISIT:
                        display.unit.visitList.initializeFromJson(message);
                        break;
                    case E_MasterListName.SERVICE:
                        display.unit.serviceList.initializeFromJson(message);
                        break;
                    case E_MasterListName.TREATMENT:
                        display.unit.treatmentList.initializeFromJson(message);
                        break;
                    case E_MasterListName.PATIENT_STATUS:
                        display.unit.patientStatusList.initializeFromJson(message);
                        break;
                    case E_MasterListName.LANGUAGE_BARRIER_LIST:
                        display.unit.languageBarrierList.initializeFromJson(message);
                        break;
                    case E_MasterListName.PASSIVE_SITTING:
                        display.unit.passiveSittingList.initializeFromJson(message);
                        break;
                }
                display.unit?.forceUpdateDetection();
                break;
            default:
                console.warn('Unknown type message: ', type, message);
        }
    }

    forceRefresh() {
        window.location.reload();
    }

    private processObservationBatch(batch: { message: any, display: C_DisplayData }[]) {
        const display = batch[0].display;
        const messages: (I_ObservationResponse & { localId: symbol })[] = batch.map(b => ({ ...b.message, localId: Symbol() }));
        display.addObservationsBulk(messages);
    }
    private processGroupedObservationBatch(batch: { message: any, display: C_DisplayData }[]) {
        const display = batch[0].display;
        const messages: (I_ObservationResponse & { localId: symbol })[] = batch.map(b => ({ ...b.message, localId: Symbol() }));
        display.addGroupedObservationsBulk(messages);
    }

    private processTreatmentBatch(batch: { message: any, display: C_DisplayData }[]) {
        const display = batch[0].display;
        const messages = batch.map(b => b.message);
        display.addTreatmentBulk(messages);
    }

    private handleAlarm(message: any, display: C_DisplayData) {
        const alarm = new C_ObservationAlarmData(message);
        display.displayInfo.pointOfCares.addAlarm(alarm);
    }

    private handleNewPoc(message: I_PointOfCareResponse, display: C_DisplayData) {
        display.patientList.setPatient(message.patient);
        display.displayInfo.pointOfCares.setPocFromResponse(message);
        const pocId = message.id;
        const layoutId = display.displayInfo.defaultLayoutId;
        if (pocId && layoutId) display.displayInfo.pointOfCares.setPocLayoutId(pocId, layoutId);
        display.displayInfo.pointOfCares.forceUpdateDetection()
    }
}
